import React from "react";
import moment from "moment";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import "./bulkSelectionPanel.css";

// Bulk-scheduling experiment (this branch only) — the right-side (Desktop/
// Tablet) / bottom-sheet (Mobile) panel a trainer uses to review and confirm
// up to `maxSlots` appointments picked directly on the Calendar grid, ported
// to match the User App's own appointment-selection panel
// (web_user_v2/src/pages/Schedule.tsx) as closely as this app's plain-CSS
// system allows — see that file for the original reference design.

const WEEKDAY_FULL = [
  "Nedelja",
  "Ponedeljak",
  "Utorak",
  "Sreda",
  "Četvrtak",
  "Petak",
  "Subota",
];
const MONTH_SHORT = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "avg",
  "sep",
  "okt",
  "nov",
  "dec",
];

const formatSlotDate = (m) =>
  `${WEEKDAY_FULL[m.day()]}, ${m.date()}. ${MONTH_SHORT[m.month()]} ${m.year()}.`;

const countLabel = (n) => `${n} ${n === 1 ? "izabran termin" : "izabrana termina"}`;
const saveLabel = (n) => (n === 1 ? "Sačuvaj termin" : `Sačuvaj ${n} termina`);
// For the "deselected every originally-booked slot" case below — wording matches the
// create/cancel success toasts SchedulerTest.jsx already shows ("Termin je otkazan" /
// "Otkazano N termina"), so the button says what it's actually about to do.
const removeAllLabel = (n) => (n === 1 ? "Otkaži termin" : `Otkaži ${n} termina`);

const BulkSelectionPanel = ({
  member,
  slots,
  maxSlots,
  saving,
  onRemove,
  onSave,
  onCancel,
  isMobile,
  // How many of `member`'s bookings this flow started with (SchedulerTest.jsx's
  // `bulkOriginalBookings.length`) — needed to tell "nothing picked yet" apart from "picked
  // slots that were all just deselected to cancel them". Both look identical from `slots.length`
  // alone (0 either way), but only the second one still has something worth saving: without
  // this, deselecting a member's last remaining slot silently fell back to the plain "Izaberite
  // termin" empty state with no Save button at all, making that cancellation unsavable.
  originalCount = 0,
}) => {
  const count = slots.length;
  const removingAllOriginals = count === 0 && originalCount > 0;

  const body = (
    <>
      <div className="hf-bulk-panel-header">
        <div className="hf-bulk-panel-heading">
          {member && (
            <span className="hf-bulk-panel-member">
              {member.first_name} {member.last_name}
            </span>
          )}
          {count === 0 && !removingAllOriginals ? (
            <span className="hf-bulk-panel-title">Izaberite termin</span>
          ) : removingAllOriginals ? (
            <span className="hf-bulk-panel-title">Uklanjate sve termine</span>
          ) : (
            <>
              <span className="hf-bulk-panel-title">{countLabel(count)}</span>
              <span className="hf-bulk-panel-subtitle">
                Najviše {maxSlots} termina odjednom
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          className="hf-bulk-panel-close"
          onClick={onCancel}
          aria-label="Otkaži zakazivanje"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      {count === 0 && !removingAllOriginals ? (
        <div className="hf-bulk-panel-empty">
          <div className="hf-bulk-panel-empty-icon">
            <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />
          </div>
          <p>Izaberite termin u rasporedu da biste nastavili.</p>
        </div>
      ) : removingAllOriginals ? (
        <>
          <div className="hf-bulk-panel-empty">
            <div className="hf-bulk-panel-empty-icon">
              <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />
            </div>
            <p>
              Svi termini su uklonjeni iz izbora — sačuvajte da biste ih zaista otkazali.
            </p>
          </div>
          <button
            type="button"
            className={`hf-bulk-panel-save${saving ? " hf-loading" : ""}`}
            disabled={saving}
            onClick={onSave}
          >
            {saving && <span className="hf-btn-spinner" />}
            {removeAllLabel(originalCount)}
          </button>
          <button type="button" className="hf-bulk-panel-cancel" onClick={onCancel}>
            Otkaži
          </button>
        </>
      ) : (
        <>
          <div className="hf-bulk-panel-list">
            {slots.map((slot) => {
              const startM = moment(slot.start);
              const endM = moment(slot.end);
              // A past appointment is read-only in this flow (see the matching lock on the
              // calendar cell itself in CardClientTest.jsx) — no remove control for it here.
              const isPast = startM.isBefore(moment());
              return (
                <div className="hf-bulk-panel-card" key={slot.id}>
                  <div className="hf-bulk-panel-card-info">
                    <div className="hf-bulk-panel-card-date">
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                      {formatSlotDate(startM)}
                    </div>
                    <div className="hf-bulk-panel-card-time">
                      <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />
                      {startM.format("HH:mm")} – {endM.format("HH:mm")}
                    </div>
                  </div>
                  {!isPast && (
                    <button
                      type="button"
                      className="hf-bulk-panel-card-remove"
                      onClick={() => onRemove(slot.id)}
                      aria-label="Ukloni termin"
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className={`hf-bulk-panel-save${saving ? " hf-loading" : ""}`}
            disabled={count === 0 || saving}
            onClick={onSave}
          >
            {saving && <span className="hf-btn-spinner" />}
            {saveLabel(count)}
          </button>
          <button
            type="button"
            className="hf-bulk-panel-cancel"
            onClick={onCancel}
          >
            Otkaži
          </button>
        </>
      )}
    </>
  );

  return (
    <div className={`hf-bulk-panel${isMobile ? " hf-bulk-panel-mobile" : ""}`}>
      {body}
    </div>
  );
};

export default BulkSelectionPanel;
